import { cn } from '../../lib/utils.ts';

/**
 * THE BUTTON RECIPE, kept out of `Button.tsx` so that file exports components only
 * (react-refresh) — and because ~40 sites style a bare `<button>` or an anchor rather
 * than rendering `<Button>`: a form's submit, a row's inline control, a link that must
 * look like a button. Each carried its own copy, so the primitive could move and they
 * would not.
 */
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'destructive-outline'
  | 'destructive'
  | 'ghost'
  | 'link';
export type ButtonSize = 'xs' | 'sm' | 'md';

const base = cn(
  'inline-flex items-center justify-center gap-2 rounded-btn font-semibold transition-colors',
  'disabled:cursor-not-allowed disabled:opacity-50',
);

const sizeClass: Record<ButtonSize, string> = {
  // `xs` is the INLINE ROW control — an admin table cell, a credential row's Withdraw. Six
  // sites already rendered `text-xs` by hand; folding them into `sm` would have grown the
  // label inside dense rows, so the size is named here rather than lost in the migration.
  // The tap target does not shrink with it: globals.css floors every button at 44px.
  xs: 'px-3 py-1.5 text-xs',
  sm: 'px-4 py-1.5 text-sm',
  md: 'px-5 py-2.5 text-sm',
};

const variantClass: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-primary-foreground shadow-btn hover:bg-primary-hover',
  secondary: 'border border-border bg-card hover:bg-accent',
  /*
   * THREE LEVELS OF DANGER, because three were already in use and collapsing them would have
   * changed what a screen says. In order of how loudly they warn:
   *
   *   danger              — a quiet destructive tint at rest (2026-08-25: was hover-only,
   *                         invisible on touch). A destructive option among ordinary ones in
   *                         a row: Remove, Disable, Leave this case, Revoke, Void.
   *   destructive-outline — destructive AT REST, but still the quieter half of a pair. The
   *                         Decline beside a Sign, the Discard beside a Complete.
   *   destructive         — solid. The confirm button of a destructive act, where the
   *                         destructive choice IS the primary one.
   */
  // /85, not /75: at 75% the resting text is #cb5555 on white — 4.23:1, under the 4.5
  // WCAG AA floor the e2e axe gate enforces. /85 is 5.11:1 and still reads softer than
  // the full-strength hover.
  danger: 'border border-destructive/25 text-destructive/85 hover:border-destructive/60 hover:text-destructive hover:bg-destructive/5',
  'destructive-outline': 'border border-destructive/40 text-destructive hover:bg-destructive/10',
  destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive-hover',
  /* Quiet at rest and named on hover — the app's five sign-out/dismiss affordances all wore
     `text-muted-foreground` by hand, so the resting colour belongs to the variant. It had no
     users before this, which is why the colour was missing rather than deliberately absent. */
  ghost: 'text-muted-foreground hover:bg-accent hover:text-foreground',
  // ALWAYS underlined: the app's majority convention, and the accessible one -
  // `text-primary` sits at 1.04:1 against `text-muted-foreground`, so underline-on-hover
  // leaves a link distinguished by hue alone at rest (WCAG 1.4.1).
  link: 'px-0 py-0 text-xs font-medium text-primary underline underline-offset-4',
};

/**
 * The button recipe, for the ~40 sites that style a bare `<button>` or an anchor rather than
 * rendering `<Button>` — a form's submit, a row's inline control, a link that must look like
 * a button. They each carried their own copy, so the primitive could move and they would not.
 * One home; the JSX stays as it is.
 */
export function buttonClass(variant: ButtonVariant = 'primary', size: ButtonSize = 'md', className?: string): string {
  return cn(base, sizeClass[size], variantClass[variant], className);
}
